export type B2BInvoiceStatus =
  | "draft"
  | "submitted"
  | "approved"
  | "booked"
  | "transferred_for_payment"
  | "paid"
  | "rejected";

export interface B2BInvoiceFile {
  id: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  url: string | null;
}

export interface B2BInvoice {
  id: string;
  employee: string;
  employee_full_name: string;
  employee_username: string;
  employee_is_vat_payer: boolean;
  employee_vat_rate: "zw" | "0" | "5" | "8" | "23" | null;
  employee_vat_rate_display: string | null;
  period_year: number;
  period_month: number;
  invoice_number: string;
  issue_date: string;
  amount_gross: string;
  invoice_managed_file: string | null;
  invoice_file: B2BInvoiceFile | null;
  invoice_file_url: string | null;
  notes: string;
  status: B2BInvoiceStatus;
  status_display: string;
  submitted_at: string | null;
  approved_by: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  rejection_reason: string;
  booked_by: string | null;
  booked_by_name: string | null;
  booked_at: string | null;
  transferred_by: string | null;
  transferred_by_name: string | null;
  transferred_at: string | null;
  paid_by: string | null;
  paid_by_name: string | null;
  paid_at: string | null;
  salary_baseline_snapshot: string | null;
  created_at: string;
  updated_at: string;
}

export interface B2BInvoiceCommentMention {
  id: string;
  full_name: string;
}

export interface B2BInvoiceComment {
  id: string;
  content: string;
  author_id: string | null;
  author_name: string;
  author_photo_url: string | null;
  mentions: B2BInvoiceCommentMention[];
  created_at: string;
}

export interface B2BInvoiceFormData {
  period_year: number;
  period_month: number;
  invoice_number: string;
  issue_date: string;
  amount_gross: string;
  notes?: string;
  invoice_file?: File | null;
}

export type LaborCostWarningLevel = "none" | "mismatch" | "missing_baseline";

export interface LaborCostWarning {
  level: LaborCostWarningLevel;
  baseline: string | null;
  submitted: string | null;
  delta_pln: string | null;
  message: string;
}

export interface LaborCostCheckParams {
  year: number;
  month: number;
  amount_gross?: string;
  employee?: string;
}

export interface MissingForMonth {
  count: number;
  missing_periods: { year: number; month: number }[];
}

/** Okres do wyboru w formularzu nowej faktury (GET /b2b-invoices/periods/): miesiąc
 *  z co najmniej jednym dniem na B2B. `required` = rozliczany fakturą (miesiąc
 *  zmiany na UoP/UZ można zafakturować, ale nie jest wymagany). */
export interface B2BInvoicePeriod {
  year: number;
  month: number;
  required: boolean;
}

// Najstarszy dopuszczalny okres rozliczeniowy — zgodnie z backendem (MIN_INVOICE_PERIOD).
export const MIN_INVOICE_PERIOD = { year: 2026, month: 5 } as const;

/** Nazwy miesięcy (mianownik) — etykiety okresów rozliczeniowych. */
export const MONTH_NAMES = [
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
] as const;
